/**
 * SaleScenarioModeler
 *
 * Quick "what-if" calculator for U/C and pending-sale deals. Sits at the top
 * of the JV Agreements tab — gives admins the ability to model:
 *   • Sale Price
 *   • Realtor Commission (% or $)
 *   • Sale Closing Costs ($)
 *   • (optional) Additional remaining expenses before close
 *
 * Hits the canonical `/api/projects/{id}/scenario-projection` endpoint which
 * runs the SAME `calculate_project_profit` engine that powers the audit-
 * verified Stewart-Fields-Road and Carolina-40-acre regression suites.
 *
 * Results displayed:
 *   • 5-step SOP waterfall (Selling → Senior Emergency → Investor Hurdle →
 *     Mgr Preferred Reimbursement → LF Hurdle → Distributable Pool)
 *   • Per-party All-In summary (cash in / cash back / net profit / ROI%)
 *
 * Debounced auto-recalc on input change (350ms) so dragging the sale price
 * slider feels instant without hammering the API.
 */
import { useState, useEffect, useReducer } from 'react';
import { TrendingUp, Calculator, RefreshCcw, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../ui/alert-dialog';
import { calculationsApi } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { toast } from 'sonner';

const fmt = (n) => formatCurrency(n || 0);
const fmtPct = (n) => `${(n || 0).toFixed(1)}%`;

// Async-state reducer. Using a reducer (instead of separate useState calls)
// keeps the side-effect dispatcher narrow — `react-hooks/set-state-in-effect`
// allows `dispatch` calls inside effects since dispatch is a stable reference
// and the rule trusts reducers as the canonical async-state pattern.
const initialAsyncState = { result: null, loading: false, error: '' };
function asyncReducer(_state, action) {
  switch (action.type) {
    case 'reset':   return { result: null, loading: false, error: '' };
    case 'loading': return { result: null, loading: true, error: '' };
    case 'success': return { result: action.payload, loading: false, error: '' };
    case 'error':   return { result: null, loading: false, error: action.payload };
    default:        return _state;
  }
}

export const SaleScenarioModeler = ({ projectId, livePurchasePrice, defaultSalePrice, onFinalized }) => {
  // Default sale price is derived from props each render (purchase price ×2
  // is the conservative starting point if no estimated sale price exists).
  // Using lazy initial state + a `key`-style remount on projectId change
  // avoids the prop-syncing useEffect that the experimental
  // react-hooks/set-state-in-effect rule rejects.
  const projectKey = projectId || 'no-project';
  const initialSalePrice = defaultSalePrice || (livePurchasePrice ? livePurchasePrice * 2 : 0);
  return (
    <SaleScenarioModelerInner
      key={projectKey}
      projectId={projectId}
      initialSalePrice={initialSalePrice}
      onFinalized={onFinalized}
    />
  );
};

const SaleScenarioModelerInner = ({ projectId, initialSalePrice, onFinalized }) => {
  const [salePrice, setSalePrice] = useState(initialSalePrice);
  const [commissionPct, setCommissionPct] = useState(6);
  const [closingCosts, setClosingCosts] = useState(0);
  const [additionalExpenses, setAdditionalExpenses] = useState(0);
  const [additionalParty, setAdditionalParty] = useState('Land_Finder');
  const [additionalBucket, setAdditionalBucket] = useState('dd');
  const [collapsed, setCollapsed] = useState(false);

  // Finalize-Sale state — opens a confirmation dialog before writing.
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [finalizeBusy, setFinalizeBusy] = useState(false);
  const [finalizePreflight, setFinalizePreflight] = useState(null);
  const [createCommissionTxn, setCreateCommissionTxn] = useState(true);
  const [createClosingTxn, setCreateClosingTxn] = useState(true);
  const [finalizeSaleDate, setFinalizeSaleDate] = useState(new Date().toISOString().slice(0, 10));

  // Async-state via reducer.
  const [asyncState, dispatch] = useReducer(asyncReducer, initialAsyncState);
  const { result, loading, error: errorMsg } = asyncState;

  // Debounced auto-recalc.
  useEffect(() => {
    if (collapsed) return undefined;
    if (!projectId || !salePrice || salePrice <= 0) {
      dispatch({ type: 'reset' });
      return undefined;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      dispatch({ type: 'loading' });
      try {
        const res = await calculationsApi.scenarioProjection(projectId, {
          sale_price: Number(salePrice),
          sale_commission_pct: Number(commissionPct) || 0,
          sale_closing_costs: Number(closingCosts) || 0,
          additional_expenses: Number(additionalExpenses) || 0,
          additional_expenses_party: additionalParty,
          additional_expenses_bucket: additionalBucket,
        });
        if (!cancelled) dispatch({ type: 'success', payload: res.data });
      } catch (err) {
        if (cancelled) return;
        const detail = err.response?.data?.detail || err.message || 'Network error';
        const status = err.response?.status;
        let msg;
        if (status === 401) {
          msg = 'Session expired — please refresh the page.';
        } else if (status && detail) {
          msg = `Server returned ${status}: ${detail}`;
        } else {
          msg = detail;
        }
        dispatch({ type: 'error', payload: msg });
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [
    projectId, salePrice, commissionPct, closingCosts,
    additionalExpenses, additionalParty, additionalBucket, collapsed,
  ]);

  // ── Finalize-Sale handlers ─────────────────────────────────────────
  const openFinalizeDialog = async () => {
    try {
      const res = await calculationsApi.finalizeSalePreflight(projectId);
      setFinalizePreflight(res.data);
      // Pre-populate the sale date from any prior real sale date so re-saves
      // don't accidentally shift the date. Falls back to today.
      const priorDate = res.data?.current?.sale_date;
      if (priorDate) setFinalizeSaleDate(priorDate.slice(0, 10));
      // Default: only check the txn-create boxes when those amounts actually
      // exist in the scenario. Avoids creating $0 ledger entries.
      setCreateCommissionTxn((salePrice * commissionPct / 100) > 0);
      setCreateClosingTxn(closingCosts > 0);
      setFinalizeOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load finalize preflight');
    }
  };

  const confirmFinalize = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setFinalizeBusy(true);
    try {
      const payload = {
        sale_price: Number(salePrice),
        sale_date: finalizeSaleDate,
        status: 'Sold',
        create_commission_txn: createCommissionTxn,
        commission_amount: salePrice * (Number(commissionPct) || 0) / 100,
        create_closing_txn: createClosingTxn,
        closing_amount: Number(closingCosts) || 0,
        selling_party: 'Land_Finder',
      };
      const res = await calculationsApi.finalizeSale(projectId, payload);
      const txnCount = (res.data.txns_created || []).length;
      toast.success(
        `Sale finalized at ${fmt(salePrice)}` +
        (txnCount > 0 ? ` (+ ${txnCount} selling txn${txnCount !== 1 ? 's' : ''})` : ''),
      );
      setFinalizeOpen(false);
      // Auto-collapse the modeler post-save and call back to parent so the
      // JV tab + the rest of the project page can refetch the new official numbers.
      setCollapsed(true);
      if (typeof onFinalized === 'function') onFinalized(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to finalize sale');
    } finally {
      setFinalizeBusy(false);
    }
  };

  const wf = result?.sop_waterfall || {};
  const inv = result?.investor || {};
  const lf = result?.land_finder || {};
  const inputs = result?.scenario_inputs || {};

  return (
    <Card className="rounded-sm border border-amber-200 bg-gradient-to-br from-amber-50/60 to-white" data-testid="sale-scenario-modeler">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Calculator className="w-4 h-4 text-amber-700" />
            Sale Scenario Modeler
            <span className="text-[10px] text-muted-foreground font-normal italic">
              Quick &quot;what-if&quot; using the SOP-compliant waterfall
            </span>
          </CardTitle>
          <button
            onClick={() => setCollapsed(c => !c)}
            className="text-xs text-amber-700 hover:underline"
            data-testid="sale-scenario-collapse-toggle"
          >
            {collapsed ? 'Show' : 'Hide'}
          </button>
        </div>
      </CardHeader>

      {!collapsed && (
        <CardContent className="space-y-4">
          {/* Inputs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Sale Price (U/C)</Label>
              <Input
                type="number"
                value={salePrice || ''}
                onChange={e => setSalePrice(parseFloat(e.target.value) || 0)}
                className="h-9 rounded-sm font-mono text-sm mt-1"
                placeholder="0"
                data-testid="scenario-sale-price"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Realtor Commission %</Label>
              <Input
                type="number"
                step="0.5"
                value={commissionPct || ''}
                onChange={e => setCommissionPct(parseFloat(e.target.value) || 0)}
                className="h-9 rounded-sm font-mono text-sm mt-1"
                placeholder="6"
                data-testid="scenario-commission-pct"
              />
              <p className="text-[9px] text-muted-foreground mt-0.5">
                = {fmt(salePrice * commissionPct / 100)}
              </p>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Sale Closing Costs</Label>
              <Input
                type="number"
                value={closingCosts || ''}
                onChange={e => setClosingCosts(parseFloat(e.target.value) || 0)}
                className="h-9 rounded-sm font-mono text-sm mt-1"
                placeholder="Transfer tax, attorney, title"
                data-testid="scenario-closing-costs"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Additional Spend (pre-close)</Label>
              <Input
                type="number"
                value={additionalExpenses || ''}
                onChange={e => setAdditionalExpenses(parseFloat(e.target.value) || 0)}
                className="h-9 rounded-sm font-mono text-sm mt-1"
                placeholder="Anticipated DD/improv"
                data-testid="scenario-additional-expenses"
              />
              {additionalExpenses > 0 && (
                <div className="flex gap-1 mt-1">
                  <select
                    value={additionalParty}
                    onChange={e => setAdditionalParty(e.target.value)}
                    className="text-[10px] h-6 rounded-sm border px-1 flex-1"
                    data-testid="scenario-additional-party"
                  >
                    <option value="Land_Finder">LF pays</option>
                    <option value="Investor">Investor pays</option>
                  </select>
                  <select
                    value={additionalBucket}
                    onChange={e => setAdditionalBucket(e.target.value)}
                    className="text-[10px] h-6 rounded-sm border px-1 flex-1"
                    data-testid="scenario-additional-bucket"
                  >
                    <option value="dd">DD</option>
                    <option value="improvements">Improv</option>
                    <option value="closing">Closing</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Headline ROI cards — investor + LF side-by-side */}
          {result ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="scenario-headline-cards">
              <div className="p-3 rounded-sm border border-emerald-200 bg-emerald-50/60">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-900">Investor</p>
                  <p className="text-[10px] text-emerald-700">
                    Tier {result.active_tier} · {fmtPct(result.investor_split_percentage)} split · {result.days_held}d held
                  </p>
                </div>
                <p className="font-mono text-xl font-bold text-emerald-700 mt-1" data-testid="scenario-investor-profit">
                  {fmt(inv.net_profit)} <span className="text-xs text-emerald-600 font-normal">profit</span>
                </p>
                <div className="grid grid-cols-3 gap-2 mt-2 text-[10px]">
                  <div>
                    <p className="text-muted-foreground">Cash In</p>
                    <p className="font-mono font-medium">{fmt(inv.total_cash_in)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Cash Back</p>
                    <p className="font-mono font-medium text-emerald-700">{fmt(inv.total_cash_back)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">ROI</p>
                    <p className="font-mono font-bold text-emerald-700" data-testid="scenario-investor-roi">{fmtPct(inv.roi_pct)}</p>
                  </div>
                </div>
              </div>

              <div className="p-3 rounded-sm border border-amber-200 bg-amber-50/60">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-900">Land Finder</p>
                  <p className="text-[10px] text-amber-700">
                    {fmtPct(100 - result.investor_split_percentage)} split
                  </p>
                </div>
                <p className="font-mono text-xl font-bold text-amber-700 mt-1" data-testid="scenario-lf-profit">
                  {fmt(lf.net_profit)} <span className="text-xs text-amber-600 font-normal">profit</span>
                </p>
                <div className="grid grid-cols-3 gap-2 mt-2 text-[10px]">
                  <div>
                    <p className="text-muted-foreground">Cash In</p>
                    <p className="font-mono font-medium">{fmt(lf.total_cash_in)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Cash Back</p>
                    <p className="font-mono font-medium text-amber-700">{fmt(lf.total_cash_back)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">ROI</p>
                    <p className="font-mono font-bold text-amber-700">{lf.total_cash_in > 0 ? fmtPct(lf.roi_pct) : '—'}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-6 text-xs" data-testid="scenario-empty-state">
              {loading ? (
                <span className="inline-flex items-center gap-2 text-muted-foreground">
                  <RefreshCcw className="w-3 h-3 animate-spin" /> Calculating...
                </span>
              ) : errorMsg ? (
                <span className="text-red-700 font-medium" data-testid="scenario-error">{errorMsg}</span>
              ) : (
                <span className="text-muted-foreground">Enter a Sale Price above to model the waterfall.</span>
              )}
            </div>
          )}

          {/* SOP waterfall breakdown */}
          {result && (
            <details className="rounded-sm border bg-white" open>
              <summary className="px-3 py-2 cursor-pointer text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5" />
                SOP Waterfall Breakdown
                <span className="text-[10px] text-muted-foreground font-normal italic ml-auto">
                  Same engine as the audit-verified live capital position
                </span>
              </summary>
              <div className="border-t divide-y text-xs" data-testid="scenario-waterfall">
                <Row label="Sale Proceeds" value={inputs.sale_price} positive bold />
                <Row label="Step 1 — Selling Expenses" sub={`Commission ${fmt(inputs.sale_commission_amount)} + Closing ${fmt(inputs.sale_closing_costs)}`} value={-wf.step1_selling_expenses} />
                {wf.step2_senior_manager_emergency_advance > 0.005 && (
                  <Row label="Step 2 — Senior Manager Emergency Advance" sub="To LF (senior preferred)" value={-wf.step2_senior_manager_emergency_advance} />
                )}
                <Row
                  label="Step 3 — Investor Hurdle Payback"
                  sub={`Capital ${fmt(wf.step3_capital_contributed)}${wf.step3_lf_paid_emd_diverted > 0.005 ? ` − LF-fronted EMD ${fmt(wf.step3_lf_paid_emd_diverted)} (to LF in Step 4)` : ''}${wf.step3_investor_paid_for_their_responsibility > 0 ? ` + Inv-paid resp ${fmt(wf.step3_investor_paid_for_their_responsibility)}` : ''}${wf.step3_investor_overpayment_refund > 0 ? ` + Overpayment refund ${fmt(wf.step3_investor_overpayment_refund)}` : ''}`}
                  value={-wf.step3_investor_hurdle_payback}
                />
                {wf.step4_manager_preferred_reimbursement > 0.005 && (
                  <Row label="Step 4 — Manager Preferred Reimbursement" sub="LF-fronted investor-responsibility expenses" value={-wf.step4_manager_preferred_reimbursement} />
                )}
                {wf.step5_lf_hurdle_payback > 0.005 && (
                  <Row label="Step 5 — LF Capital Hurdle Payback" sub="LF's own contractual responsibility expenses" value={-wf.step5_lf_hurdle_payback} />
                )}
                <Row label="Distributable Pool" value={wf.distributable_pool} positive bold highlight />
                <Row label={`Investor Profit Share (${fmtPct(result.investor_split_percentage)})`} value={wf.investor_profit_share} positive />
                <Row label={`LF Profit Share (${fmtPct(100 - result.investor_split_percentage)})`} value={wf.lf_profit_net} positive />
              </div>
            </details>
          )}

          {loading && result && (
            <p className="text-[10px] text-muted-foreground text-center italic">Recalculating...</p>
          )}

          {/* Save as Official Sale — copies the scenario into the project's
              persistent fields once U/C closes. Owner-only (the modeler
              itself is already gated to owners; this just adds the action).
              The button is enabled only when we have a fresh result AND a
              positive sale price — guards against accidental zero-out. */}
          {result && (
            <div className="pt-3 border-t border-amber-200/60 flex items-center justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold text-amber-900">Ready to commit this scenario?</p>
                <p className="text-[10px] text-muted-foreground">
                  Updates the project&apos;s sale price + date and optionally creates the commission/closing txns.
                </p>
              </div>
              <Button
                onClick={openFinalizeDialog}
                disabled={!salePrice || salePrice <= 0 || finalizeBusy}
                className="bg-[#1A2F23] hover:bg-[#243d2e] text-white rounded-sm text-xs"
                data-testid="scenario-finalize-open-btn"
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                Save as Official Sale
              </Button>
            </div>
          )}
        </CardContent>
      )}

      {/* ── Finalize-Sale confirmation dialog ──────────────────────── */}
      <AlertDialog open={finalizeOpen} onOpenChange={setFinalizeOpen}>
        <AlertDialogContent className="max-w-lg rounded-sm" data-testid="scenario-finalize-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Save as Official Sale</AlertDialogTitle>
            <AlertDialogDescription className="text-xs space-y-1">
              <span className="block">
                This will write the modeled scenario to the project&apos;s persistent
                sale_price + sale_date and (optionally) create selling-bucket
                transactions for the commission and closing costs.
              </span>
              {finalizePreflight?.warn_duplicates && (
                <span className="block mt-2 px-2 py-1.5 bg-amber-50 border border-amber-200 rounded-sm text-amber-900 text-[11px]" data-testid="finalize-duplicate-warning">
                  ⚠ This project already has <strong>{finalizePreflight.existing_selling_txns.length}</strong> selling-bucket transaction{finalizePreflight.existing_selling_txns.length !== 1 ? 's' : ''}.
                  Uncheck the commission/closing boxes below to avoid duplicates.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3 text-xs">
            {/* Changes to the project record */}
            <div className="rounded-sm border bg-slate-50/40 p-3">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">Project Fields</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-muted-foreground">Sale Price:</span>{' '}
                  <span className="font-mono font-semibold" data-testid="finalize-sale-price">{fmt(salePrice)}</span>
                  {finalizePreflight?.current?.sale_price > 0 && Math.abs((finalizePreflight.current.sale_price || 0) - salePrice) > 0.01 && (
                    <span className="block text-[10px] text-muted-foreground">
                      was: <span className="font-mono">{fmt(finalizePreflight.current.sale_price)}</span>
                    </span>
                  )}
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Sale Date</Label>
                  <Input
                    type="date"
                    value={finalizeSaleDate}
                    onChange={e => setFinalizeSaleDate(e.target.value)}
                    className="h-7 rounded-sm font-mono text-xs"
                    data-testid="finalize-sale-date"
                  />
                </div>
                <div className="col-span-2">
                  <span className="text-muted-foreground">Status will change to:</span>{' '}
                  <span className="font-semibold">Sold</span>
                  {finalizePreflight?.current?.status && finalizePreflight.current.status !== 'Sold' && (
                    <span className="text-[10px] text-muted-foreground"> (was: {finalizePreflight.current.status})</span>
                  )}
                </div>
              </div>
            </div>

            {/* Transactions to create */}
            <div className="rounded-sm border bg-slate-50/40 p-3 space-y-2">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Transactions To Create</p>
              <label className="flex items-start gap-2 cursor-pointer" data-testid="finalize-create-commission-toggle">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={createCommissionTxn}
                  onChange={e => setCreateCommissionTxn(e.target.checked)}
                  data-testid="finalize-create-commission-checkbox"
                />
                <div className="flex-1">
                  <p className="font-medium">Real Estate Commission</p>
                  <p className="text-[10px] text-muted-foreground">
                    {fmt(salePrice * commissionPct / 100)} · Expense / Selling bucket / LF-paid
                  </p>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer" data-testid="finalize-create-closing-toggle">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={createClosingTxn && closingCosts > 0}
                  disabled={closingCosts <= 0}
                  onChange={e => setCreateClosingTxn(e.target.checked)}
                  data-testid="finalize-create-closing-checkbox"
                />
                <div className="flex-1">
                  <p className={`font-medium ${closingCosts <= 0 ? 'text-muted-foreground' : ''}`}>Sale Closing Costs</p>
                  <p className="text-[10px] text-muted-foreground">
                    {closingCosts > 0 ? `${fmt(closingCosts)} · Expense / Selling bucket / LF-paid` : '— skipped (enter Closing Costs in the modeler first)'}
                  </p>
                </div>
              </label>
            </div>

            <p className="text-[10px] text-muted-foreground italic">
              An audit_log entry is written with the prior values so this action is reversible if you need to roll back.
            </p>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={finalizeBusy} data-testid="finalize-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmFinalize}
              disabled={finalizeBusy}
              className="bg-[#1A2F23] hover:bg-[#243d2e]"
              data-testid="finalize-confirm-btn"
            >
              {finalizeBusy ? 'Saving...' : 'Confirm & Save'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

// Single waterfall row. Negative values render red; positive emerald.
const Row = ({ label, sub, value, positive, bold, highlight }) => {
  const v = Number(value) || 0;
  const isNeg = v < 0;
  return (
    <div className={`flex items-start justify-between px-3 py-2 ${highlight ? 'bg-emerald-50/40' : ''}`}>
      <div className="flex-1">
        <p className={`${bold ? 'font-semibold' : ''}`}>{label}</p>
        {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
      </div>
      <p
        className={`font-mono ${bold ? 'text-sm font-semibold' : 'text-xs'} ${isNeg ? 'text-red-700' : positive ? 'text-emerald-700' : 'text-slate-700'}`}
      >
        {isNeg ? '−' : positive && v > 0 ? '+' : ''}{fmt(Math.abs(v))}
      </p>
    </div>
  );
};

export default SaleScenarioModeler;
